import React, { useState } from "react";
import {
  auth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { StyledForm } from "./style";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getFirebaseErrorMessage } from "features/util";
import { formValidation, formatPhoneNumber } from "utils";
import toast from "react-hot-toast";

const SignUpForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    name: "",
    phoneNumber: "",
    company: "",
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    if (name === "confirmEmail" && value !== formState.email) {
      newErrors.confirmEmail = "Emails do not match";
    } else if (name === "confirmEmail") {
      delete newErrors.confirmEmail;
    }
    if (name === "confirmPassword" && value !== formState.password) {
      newErrors.confirmPassword = "Passwords do not match";
    } else if (name === "confirmPassword") {
      delete newErrors.confirmPassword;
    }
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue =
      name === "phoneNumber" ? formatPhoneNumber(value) : value;

    setFormState((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    let newErrors = { ...errors };

    if (name === "email" || name === "confirmEmail") {
      if (formState.confirmEmail && value !== formState.confirmEmail) {
        newErrors.confirmEmail = "Emails do not match";
      } else {
        delete newErrors.confirmEmail;
      }
    }

    if (name === "password" || name === "confirmPassword") {
      if (formState.confirmPassword && value !== formState.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      } else {
        delete newErrors.confirmPassword;
      }
    }

    setErrors(newErrors);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const validationErrors = formValidation(formState);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (formState.email !== formState.confirmEmail) {
      setErrors((prev) => ({
        ...prev,
        confirmEmail: "Emails do not match",
      }));
      return;
    }
    if (formState.password !== formState.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formState.email,
        formState.password
      );

      // 이메일 인증 메일 전송
      try {
        await sendEmailVerification(userCredential.user);
        toast.success("인증 메일이 발송되었습니다. 이메일을 확인해주세요.", {
          duration: 5000,
        });
      } catch (emailError) {
        console.error("Error sending verification email:", emailError);
        toast.error(
          "인증 메일 발송에 실패했습니다. 나중에 재전송할 수 있습니다.",
          {
            duration: 5000,
          }
        );
      }

      // 사용자 정보를 sessionStorage에 임시 저장 (이메일 인증 완료 후 사용)
      sessionStorage.setItem(
        "pendingSignUp",
        JSON.stringify({
          name: formState.name,
          phoneNumber: formState.phoneNumber,
          company: formState.company,
        })
      );

      // 이메일 인증 페이지로 이동 (GraphQL mutation은 인증 완료 후 실행)
      navigate("/verify-email");
    } catch (firebaseError) {
      const errorMessage = getFirebaseErrorMessage(firebaseError.code);
      console.error("Signup Error:", firebaseError);

      // GraphQL mutation 에러는 이미 처리했으므로 Firebase 에러만 처리
      if (firebaseError.code && firebaseError.code.startsWith("auth/")) {
        toast.error(errorMessage, { duration: 5000 });
      } else if (!firebaseError.message?.includes("회원가입 처리")) {
        toast.error("회원가입 중 오류가 발생했습니다. 다시 시도해주세요.", {
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };
  const handleShowPassword = () => setShowPassword(!showPassword);
  return (
    <form onSubmit={handleSignUp}>
      <StyledForm>
        <Typography variant="subtitle" fontWeight="bold" className="highlight">
          회원 가입
        </Typography>
        <Box className="column-container">
          <Box>
            <TextField
              type="email"
              label="이메일"
              name="email"
              variant="outlined"
              value={formState.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              margin="normal"
              disabled={loading}
              required
            />
            <TextField
              type="email"
              label="이메일 확인"
              name="confirmEmail"
              variant="outlined"
              value={formState.confirmEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.confirmEmail}
              helperText={errors.confirmEmail}
              fullWidth
              margin="normal"
              disabled={loading}
              required
              InputProps={{
                endAdornment:
                  formState.confirmEmail &&
                  (formState.email === formState.confirmEmail ? (
                    <InputAdornment position="end">
                      <CheckCircleOutlineIcon color="success" />
                    </InputAdornment>
                  ) : (
                    formState.confirmEmail && (
                      <InputAdornment position="end">
                        <ErrorOutlineIcon color="error" />
                      </InputAdornment>
                    )
                  )),
              }}
            />
          </Box>
          <Box>
            <TextField
              type={showPassword ? "text" : "password"}
              label="비밀번호"
              name="password"
              variant="outlined"
              value={formState.password}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              margin="normal"
              disabled={loading}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword}>
                      {showPassword ? (
                        <VisibilityOutlinedIcon />
                      ) : (
                        <VisibilityOffOutlinedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              type={showPassword ? "text" : "password"}
              label="비밀번호 확인"
              name="confirmPassword"
              variant="outlined"
              value={formState.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              margin="normal"
              disabled={loading}
              required
              InputProps={{
                endAdornment:
                  formState.confirmPassword &&
                  (formState.password === formState.confirmPassword ? (
                    <InputAdornment position="end">
                      <CheckCircleOutlineIcon color="success" />
                    </InputAdornment>
                  ) : (
                    formState.confirmPassword && (
                      <InputAdornment position="end">
                        <ErrorOutlineIcon color="error" />
                      </InputAdornment>
                    )
                  )),
              }}
            />
          </Box>
        </Box>
        <TextField
          label="이름"
          type="text"
          name="name"
          variant="outlined"
          value={formState.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          disabled={loading}
        />
        <TextField
          label="회사 이름"
          name="company"
          variant="outlined"
          value={formState.company}
          onChange={handleChange}
          fullWidth
          margin="normal"
          disabled={loading}
          required
        />
        <TextField
          label="전화 번호"
          name="phoneNumber"
          variant="outlined"
          value={formState.phoneNumber}
          onChange={handleChange}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          fullWidth
          margin="normal"
          type="tel"
          disabled={loading}
          placeholder="010-1234-5678"
          required
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading || Object.keys(errors).length > 0}
          sx={{
            width: "100%",
            color: "white",
            fontWeight: "bold",
            borderRadius: "2rem",
            paddingTop: "0.75rem",
            paddingBottom: "0.75rem",
          }}
        >
          <Typography variant="body">회원 가입</Typography>
          {loading && (
            <CircularProgress
              size={24}
              color="inherit"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Button>
      </StyledForm>
    </form>
  );
};

export default SignUpForm;
